import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isToday from 'date-fns/isToday';
import PropTypes from 'prop-types';
import {
  ClockIcon,
  constituentsIcon,
  KanbanCards,
  PersonOutlineIcon,
} from '@/assets/images';
import Loader from '@/components/common/Loader';
import PageSizeSelect from '@/components/common/PageSizeSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@/components/common/ResponsiveTable';
import ShowForViews from '@/components/common/ShowForViews';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import UserViewEnums from '@/constants/users/UserViewEnums';
import { DEFAULT_DATE_FORMAT } from '@/lib/constants/common';
import translate from '@/lib/intl/translate';
import { dateStringToDate } from '@/utils/dates';
import useTaskListView from '../lib/useTaskListView';
import { TaskSortBy as SortBy } from '../tasksConfig';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import TaskListFilterControls from './TaskListFilterControls';
import { useTasksContext } from './TasksProvider';

function TasksListView({ isTasksPage = false }) {
  const {
    onCheckboxChange,
    onConstituentClick,
    onCreateTaskClick,
    onFilterUpdate,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onSortClick,
  } = useTaskListView();
  const { data, error, isLagging, isLoading, query, totalHits, updateQuery } =
    useTasksContext();

  React.useEffect(() => {
    // If we are on a page greater than 1 and we have only 1 task, then deleting
    // it will result in no more tasks to show on the current view. In that
    // event, let's move the viewer back 1 page.
    if (query.page > 1 && data && !data.length) {
      updateQuery({ page: query.page - 1 });
    }
  }, [data, query, updateQuery]);

  const renderDueDate = ({ dueDate, isCompleted }) => {
    if (!dueDate) return null;

    const date = dateStringToDate(dueDate);
    let className = 'text-grey-900 md:text-text-primary';
    if (!isCompleted && isBefore(date, endOfDay(new Date()))) {
      className = 'text-error-main';
    }
    if (isToday(date)) {
      className = 'font-semibold';
    }
    return <div className={className}>{format(date, DEFAULT_DATE_FORMAT)}</div>;
  };
  const cellClasses =
    'md:align-top font-normal text-sm p-0 md:py-3 border-0 md:border-t md:border-b md:border-solid border-grey-300';
  const totalPages = Math.ceil((totalHits || 0) / query.pageSize);

  return (
    <div>
      <TaskListFilterControls
        filters={query}
        isTasksPage={isTasksPage}
        onFilterUpdate={onFilterUpdate}
      />
      <div className="flex items-center">
        <h2 className="type-h3">
          {translate('TASKS')} ({totalHits?.toString() || '-'})
        </h2>
        <PageSizeSelect
          value={query.pageSize}
          className="ml-auto mb-0"
          onChange={onPageSizeChange}
        />
      </div>
      <TableContainer>
        <Table className="border-separate" style={{ borderSpacing: '0 8px' }}>
          <TableHead className="hidden border-0 md:table-header-group">
            <TableRow>
              <TableCell className="w-1/2 capitalize">
                <TableSortLabel
                  active={query.sortBy === SortBy.TASK_TITLE}
                  direction={query.sortOrder}
                  onClick={onSortClick(SortBy.TASK_TITLE)}
                >
                  {translate('TITLE_LABEL')}
                </TableSortLabel>
              </TableCell>
              <TableCell className="w-1/6 pl-0 text-left capitalize">
                {translate('TASKS_ASSIGNEE')}
              </TableCell>
              <TableCell className="w-1/6 pl-0 text-left capitalize">
                {translate('TASKS_CONSTITUENT')}
              </TableCell>
              <TableCell className="w-1/6 pl-0 text-left capitalize">
                <TableSortLabel
                  active={query.sortBy === SortBy.DUE_DATE}
                  direction={query.sortOrder}
                  onClick={onSortClick(SortBy.DUE_DATE)}
                >
                  {translate('TASKS_DUE_DATE')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            className={classNames('md:table-row-group', {
              'opacity-60': isLagging,
            })}
          >
            {data?.map((task) => (
              <TableRow
                key={task.taskId}
                onClick={() => onRowClick(task)}
                className="mb-3 flex w-full flex-wrap items-center rounded border border-grey-300 p-3 hover:cursor-pointer hover:bg-grey-100 md:mb-0 md:table-row md:border-0 md:p-0"
                data-testid={`tasks-list-item-${task.taskId}`}
              >
                <TableCell className="order-1 flex w-full items-center rounded-l border-0 border-grey-300 p-0 align-top text-base md:table-cell md:w-1/2 md:border-t md:border-b md:border-l md:border-solid md:text-sm">
                  <div className="flex">
                    <Tooltip
                      arrow
                      classes={{
                        arrow: 'text-text-primary opacity-80',
                        tooltip: 'bg-text-primary opacity-80 rounded-sm m-0',
                      }}
                      enterDelay={500}
                      leaveDelay={200}
                      placement="bottom"
                      title={
                        task.isCompleted
                          ? translate('TASKS_MARK_AS_INCOMPLETE')
                          : translate('TASKS_MARK_AS_COMPLETE')
                      }
                    >
                      <div>
                        <ShowForViews
                          showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}
                          fallback={
                            <Checkbox checked={task.isCompleted} disabled />
                          }
                        >
                          <Checkbox
                            data-testid={`task-list-item-checkbox-${task.taskTitle}`}
                            checked={task.isCompleted}
                            color="primary"
                            id={`task-list-item-checkbox-${task.taskTitle}`}
                            name={task.taskTitle}
                            onClick={(e) => {
                              onCheckboxChange(e, task);
                            }}
                            className="pl-0 md:pl-2"
                          />
                        </ShowForViews>
                      </div>
                    </Tooltip>

                    <div
                      className="py-2.5 md:py-3"
                      data-testid={`tasks-list-item-${task.taskId}-title`}
                    >
                      {task.taskTitle}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className={classNames(
                    cellClasses,
                    {
                      'w-1/2': (task.developers?.length ?? 0) > 0,
                      'pl-8 md:pl-0': !task.dueDate,
                    },
                    'order-4 flex-col text-left md:order-3 md:w-1/6',
                  )}
                >
                  {(task.developers || []).map((developer) => (
                    <div
                      key={developer.userId}
                      className="flex items-center text-grey-900 md:text-text-primary"
                    >
                      <PersonOutlineIcon className="mr-1 text-grey-900 md:hidden" />
                      <div>{developer.displayName}</div>
                    </div>
                  ))}
                </TableCell>
                <TableCell
                  className={classNames(
                    cellClasses,
                    'order-5 w-1/2 pl-8 pt-1 text-left md:order-4 md:w-1/6 md:pl-0',
                  )}
                >
                  {(task.constituents || []).map((constituent) => (
                    <div
                      className="flex items-center"
                      key={constituent.constituentId}
                    >
                      <img
                        className="mr-1 md:hidden"
                        height="14px"
                        width="14px"
                        src={constituentsIcon}
                        alt=""
                      />
                      <Link
                        to={generatePath(AuthRoutes.CONSTITUENT, {
                          constituentId: constituent.constituentId,
                        })}
                        onClick={(e) => onConstituentClick(e, constituent)}
                        className="text-sm font-normal text-primary-main"
                      >
                        {constituent.displayName}
                      </Link>
                    </div>
                  ))}
                </TableCell>
                <TableCell
                  className={classNames(
                    cellClasses,
                    { 'w-1/2': task.dueDate },
                    'order-3 rounded-r pr-4 pl-8 text-left md:order-5 md:w-1/6 md:border-r md:pl-0',
                  )}
                >
                  <div className="flex items-center justify-start">
                    {task.dueDate && (
                      <ClockIcon className="mr-1 text-grey-900 md:hidden" />
                    )}
                    {renderDueDate(task)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading && <Loader isCentered className="my-4" />}
      {data?.length === 0 && (
        <div className="flex justify-center pt-11">
          <div className="text-center">
            <img src={KanbanCards} className="mx-auto my-3" alt="" />

            <p className="type-body2">
              {translate('TASKS_NO_TASKS')}&nbsp;
              <ShowForViews
                showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}
              >
                {translate('TASKS_CLICK_TO_CREATE')}
              </ShowForViews>
            </p>
            <ShowForViews showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="button"
                className="mt-5 h-12"
                onClick={onCreateTaskClick}
              >
                {translate('TASKS_CREATE_TASK')}
              </Button>
            </ShowForViews>
          </div>
        </div>
      )}
      {error && (
        <div className="pt-4 text-center font-normal">
          {translate('TASKS_FETCH_ERROR')}
        </div>
      )}
      <div className="mt-6 flex flex-col items-center">
        {!!data?.length && (
          <Pagination
            count={totalPages}
            page={query.page}
            onChange={onPageChange}
            shape="rounded"
          />
        )}
      </div>
    </div>
  );
}

TasksListView.propTypes = {
  isTasksPage: PropTypes.bool,
};

export default TasksListView;
